import { HttpClient } from "@utils/index";
import type {
  CategoryModel,
  CategoryViewModel,
  CreateCategoryPayload,
  UpdateCategoryPayload,
} from "@models/category";
import {
  buildCategoryViewModels,
  buildCategoryViewModel,
  buildCreateCategoryRequest,
  buildUpdateCategoryRequest,
} from "@utils/builders/category";

const BASE_URL = import.meta.env.VITE_EXTRACTION_API_BASE_URL;

if (!BASE_URL) {
  throw new Error(
    "Missing required environment variable: VITE_EXTRACTION_API_BASE_URL. " +
      "Please check your .env file and ensure the Intelligence Engine API URL is set.",
  );
}

class CategoryApi {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient(`${BASE_URL}`);
  }

  async getCategories(): Promise<CategoryViewModel[]> {
    const response = await this.httpClient.makeAuthenticatedRequest<
      CategoryModel[]
    >(`/categories`, { method: "GET" });
    return buildCategoryViewModels(response);
  }

  async createCategory(
    payload: CreateCategoryPayload,
  ): Promise<CategoryViewModel> {
    const apiRequest = buildCreateCategoryRequest(payload);
    const response =
      await this.httpClient.makeAuthenticatedRequest<CategoryModel>(
        `/categories`,
        {
          method: "POST",
          body: JSON.stringify(apiRequest),
        },
      );
    return buildCategoryViewModel(response);
  }

  async updateCategory(
    categoryId: string,
    payload: UpdateCategoryPayload,
  ): Promise<CategoryViewModel> {
    const apiRequest = buildUpdateCategoryRequest(payload);
    const response =
      await this.httpClient.makeAuthenticatedRequest<CategoryModel>(
        `/categories/${encodeURIComponent(categoryId)}`,
        {
          method: "PUT",
          body: JSON.stringify(apiRequest),
        },
      );
    return buildCategoryViewModel(response);
  }

  async deleteCategory(categoryId: string): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest(
      `/categories/${encodeURIComponent(categoryId)}`,
      { method: "DELETE" },
    );
  }
}

export { CategoryApi };

export const categoryApi = new CategoryApi();
